import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import './EnquiriesAdmin.css';

const formatEnqId = (enq) => {
    const prefix = enq.type === 'general' ? 'GEN' : 'ENQ';
    return `${prefix}-${String(enq.id).padStart(5, '0')}`;
};

const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const formatDateTime = (d) => new Date(d).toLocaleString('en-GB');

const FILTERS = ['all', 'unreplied', 'replied', 'quarantine'];

const Badge = ({ count, grey }) =>
    count > 0 ? <span className={`enq-badge${grey ? ' enq-badge-grey' : ''}`}>{count}</span> : null;

function EnquiriesAdmin() {
    const [enquiries, setEnquiries] = useState([]);
    const [quarantine, setQuarantine] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const [selected, setSelected] = useState([]);
    const [selectedEnq, setSelectedEnq] = useState(null);

    // Sorting
    const [sortCol, setSortCol] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');

    // Purge modal
    const [purgeModal, setPurgeModal] = useState(null);
    const [purgeInput, setPurgeInput] = useState('');
    const [purgeError, setPurgeError] = useState('');

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [activeRes, quarantineRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/enquiries`),
                axios.get(`${API_BASE_URL}/api/enquiries/quarantine`),
            ]);
            setEnquiries(activeRes.data);
            setQuarantine(quarantineRes.data);
        } catch (err) {
            setError('Failed to load enquiries.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Sorting ───────────────────────────────────────────────────────────────
    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
    };

    const sortData = (data) => {
        return [...data].sort((a, b) => {
            let aVal = a[sortCol];
            let bVal = b[sortCol];
            if (sortCol === 'created_at') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // ── Filtered + sorted data ────────────────────────────────────────────────
    const filteredData = (() => {
        if (filter === 'quarantine') return sortData(quarantine);
        let data = enquiries;
        if (filter === 'unreplied') data = data.filter((e) => !e.replied);
        if (filter === 'replied') data = data.filter((e) => e.replied);
        return sortData(data);
    })();

    const unrepliedCount = enquiries.filter((e) => !e.replied).length;
    const quarantineCount = quarantine.length;

    // ── Selection ─────────────────────────────────────────────────────────────
    const toggleSelect = (id) => {
        setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        const ids = filteredData.map((e) => e.id);
        setSelected((prev) => prev.length === ids.length ? [] : ids);
    };

    const allSelected = filteredData.length > 0 && selected.length === filteredData.length;

    // ── Replied toggle ────────────────────────────────────────────────────────
    const toggleReplied = async (enq) => {
        try {
            const res = await axios.patch(`${API_BASE_URL}/api/enquiries/${enq.id}/replied`, { replied: !enq.replied });
            setEnquiries((prev) => prev.map((e) => e.id === enq.id ? res.data : e));
            if (selectedEnq?.id === enq.id) setSelectedEnq(res.data);
        } catch (err) {
            console.error('Toggle replied failed:', err);
        }
    };

    // ── Soft delete ───────────────────────────────────────────────────────────
    const softDelete = async (id) => {
        try {
            await axios.delete(`${API_BASE_URL}/api/enquiries/${id}`);
            setEnquiries((prev) => prev.filter((e) => e.id !== id));
            setSelected((prev) => prev.filter((x) => x !== id));
            if (selectedEnq?.id === id) setSelectedEnq(null);
            fetchAll();
        } catch (err) {
            console.error('Soft delete failed:', err);
        }
    };

    // ── Bulk soft delete ──────────────────────────────────────────────────────
    const bulkSoftDelete = async () => {
        if (selected.length === 0) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/enquiries/bulk`, { data: { ids: selected } });
            setSelected([]);
            if (selected.includes(selectedEnq?.id)) setSelectedEnq(null);
            fetchAll();
        } catch (err) {
            console.error('Bulk soft delete failed:', err);
        }
    };

    // ── Restore ───────────────────────────────────────────────────────────────
    const restore = async (id) => {
        try {
            await axios.patch(`${API_BASE_URL}/api/enquiries/${id}/restore`);
            fetchAll();
        } catch (err) {
            console.error('Restore failed:', err);
        }
    };

    // ── Purge ─────────────────────────────────────────────────────────────────
    const openPurgeModal = (ids, mode) => {
        setPurgeModal({ ids, mode });
        setPurgeInput('');
        setPurgeError('');
    };

    const confirmPurge = async () => {
        if (purgeInput !== 'DELETE') {
            setPurgeError('You must type DELETE to confirm.');
            return;
        }
        try {
            if (purgeModal.mode === 'single') {
                await axios.delete(`${API_BASE_URL}/api/enquiries/${purgeModal.ids[0]}/purge`);
            } else {
                await axios.delete(`${API_BASE_URL}/api/enquiries/bulk-purge`, { data: { ids: purgeModal.ids } });
            }
            setPurgeModal(null);
            setSelected([]);
            if (purgeModal.ids.includes(selectedEnq?.id)) setSelectedEnq(null);
            fetchAll();
        } catch (err) {
            setPurgeError('Failed to permanently delete. Please try again.');
            console.error(err);
        }
    };

    // ── Sort icon ─────────────────────────────────────────────────────────────
    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span className="enq-sort-icon">↕</span>;
        return <span className="enq-sort-icon-active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    const isQuarantineView = filter === 'quarantine';

    return (
        <div className="enq-admin">
            {/* ── Heading ── */}
            <div className="enq-admin-heading">
                <h1>✉️ Customer Enquiries</h1>
                <Badge count={unrepliedCount} />
            </div>
            <p className="enq-admin-subtext">
                Manage all enquiries submitted via the contact form and product pages.
            </p>

            {/* ── Filter toggles ── */}
            <div className="enq-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f}
                        onClick={() => { setFilter(f); setSelected([]); }}
                        className={`enq-filter-btn${filter === f ? ' enq-filter-btn-active' : ''}`}
                    >
                        {f === 'all' && 'All'}
                        {f === 'unreplied' && <>Unreplied <Badge count={unrepliedCount} /></>}
                        {f === 'replied' && 'Replied'}
                        {f === 'quarantine' && <>🗑 Quarantine <Badge count={quarantineCount} grey /></>}
                    </button>
                ))}
                <button onClick={fetchAll} className="enq-refresh-btn">↻ Refresh</button>
            </div>

            {/* ── Bulk actions ── */}
            {selected.length > 0 && (
                <div className="enq-bulk-bar">
                    <span className="enq-bulk-count">{selected.length} selected</span>
                    {!isQuarantineView && (
                        <button onClick={bulkSoftDelete} className="enq-bulk-btn enq-bulk-btn-quarantine">
                            🗑 Move to Quarantine
                        </button>
                    )}
                    {isQuarantineView && (
                        <button onClick={() => openPurgeModal(selected, 'bulk')} className="enq-bulk-btn enq-bulk-btn-purge">
                            ⚠️ Permanently Delete Selected
                        </button>
                    )}
                    <button onClick={() => setSelected([])} className="enq-bulk-btn enq-bulk-btn-clear">
                        Clear
                    </button>
                </div>
            )}

            {error && <p style={{ color: 'red' }}>{error}</p>}

            {loading ? (
                <p className="enq-empty">Loading enquiries...</p>
            ) : (
                <div className="enq-table-layout">

                    {/* ── Table ── */}
                    <div className="enq-table-card">
                        {filteredData.length === 0 ? (
                            <p className="enq-empty">No enquiries to show.</p>
                        ) : (
                            <table className="enq-table">
                                <thead>
                                <tr>
                                    <th className="enq-th enq-th-checkbox">
                                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                                    </th>
                                    <th className="enq-th" onClick={() => handleSort('id')}>
                                        Ref <SortIcon col="id" />
                                    </th>
                                    <th className="enq-th" onClick={() => handleSort('type')}>
                                        Type <SortIcon col="type" />
                                    </th>
                                    <th className="enq-th" onClick={() => handleSort('name')}>
                                        Name <SortIcon col="name" />
                                    </th>
                                    <th className="enq-th enq-th-static">Email / Phone</th>
                                    {!isQuarantineView && (
                                        <th className="enq-th enq-th-static">Product / Stock</th>
                                    )}
                                    <th className="enq-th" onClick={() => handleSort('created_at')}>
                                        Date <SortIcon col="created_at" />
                                    </th>
                                    {!isQuarantineView && (
                                        <th className="enq-th enq-th-static">Replied</th>
                                    )}
                                    {isQuarantineView && (
                                        <th className="enq-th" onClick={() => handleSort('days_remaining')}>
                                            Days Left <SortIcon col="days_remaining" />
                                        </th>
                                    )}
                                    <th className="enq-th enq-th-static">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredData.map((enq) => {
                                    const isSelected = selected.includes(enq.id);
                                    const isActive = selectedEnq?.id === enq.id;
                                    const rowClass = `enq-row ${isActive ? 'enq-row-active' : isSelected ? 'enq-row-selected' : 'enq-row-default'}`;
                                    return (
                                        <tr
                                            key={enq.id}
                                            className={rowClass}
                                            onClick={() => setSelectedEnq(isActive ? null : enq)}
                                        >
                                            <td className="enq-td" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSelect(enq.id)}
                                                />
                                            </td>
                                            <td className="enq-td enq-td-ref">{formatEnqId(enq)}</td>
                                            <td className="enq-td">
                                                    <span className={`enq-type-badge ${enq.type === 'general' ? 'enq-type-general' : 'enq-type-product'}`}>
                                                        {enq.type === 'general' ? 'General' : 'Product'}
                                                    </span>
                                            </td>
                                            <td className="enq-td enq-td-name">{enq.name}</td>
                                            <td className="enq-td enq-td-contact">
                                                {enq.email && <div>{enq.email}</div>}
                                                {enq.phone && <div className="enq-td-contact-phone">{enq.phone}</div>}
                                                {!enq.email && !enq.phone && <span className="enq-td-empty">—</span>}
                                            </td>
                                            {!isQuarantineView && (
                                                <td className="enq-td enq-td-product">
                                                    {enq.product_name
                                                        ? <>
                                                            <span className="enq-td-product-name">{enq.product_name}</span>
                                                            {enq.stock_number && <><br /><span className="enq-td-product-stock">{enq.stock_number}</span></>}
                                                        </>
                                                        : <span className="enq-td-empty">—</span>
                                                    }
                                                </td>
                                            )}
                                            <td className="enq-td enq-td-date">{formatDate(enq.created_at)}</td>
                                            {!isQuarantineView && (
                                                <td className="enq-td" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => toggleReplied(enq)}
                                                        className={`enq-replied-btn ${enq.replied ? 'enq-replied-yes' : 'enq-replied-no'}`}
                                                    >
                                                        {enq.replied ? '✓ Replied' : 'Pending'}
                                                    </button>
                                                </td>
                                            )}
                                            {isQuarantineView && (
                                                <td className="enq-td">
                                                        <span className={enq.days_remaining <= 5 ? 'enq-td-days-urgent' : 'enq-td-days-normal'}>
                                                            {enq.days_remaining}d
                                                        </span>
                                                </td>
                                            )}
                                            <td className="enq-td" onClick={(e) => e.stopPropagation()}>
                                                <div className="enq-row-actions">
                                                    {!isQuarantineView && (
                                                        <button
                                                            onClick={() => softDelete(enq.id)}
                                                            title="Move to quarantine"
                                                            className="enq-action-btn enq-action-btn-delete"
                                                        >🗑</button>
                                                    )}
                                                    {isQuarantineView && (
                                                        <>
                                                            <button
                                                                onClick={() => restore(enq.id)}
                                                                title="Restore"
                                                                className="enq-action-btn enq-action-btn-restore"
                                                            >↩ Restore</button>
                                                            <button
                                                                onClick={() => openPurgeModal([enq.id], 'single')}
                                                                title="Permanently delete"
                                                                className="enq-action-btn enq-action-btn-purge"
                                                            >⚠️ Delete</button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* ── Detail panel ── */}
                    {selectedEnq && (
                        <div className="enq-detail-panel">
                            <div className="enq-detail-header">
                                <div>
                                    <div className="enq-detail-ref">{formatEnqId(selectedEnq)}</div>
                                    <div className="enq-detail-date">{formatDateTime(selectedEnq.created_at)}</div>
                                </div>
                                <button onClick={() => setSelectedEnq(null)} className="enq-detail-close">✕</button>
                            </div>

                            <div className="enq-detail-type">
                                <span className={`enq-type-badge ${selectedEnq.type === 'general' ? 'enq-type-general' : 'enq-type-product'}`}>
                                    {selectedEnq.type === 'general' ? 'General Enquiry' : 'Product Enquiry'}
                                </span>
                            </div>

                            <div className="enq-detail-field">
                                <div className="enq-detail-label">NAME</div>
                                <div className="enq-detail-value enq-detail-value-bold">{selectedEnq.name}</div>
                            </div>
                            {selectedEnq.email && (
                                <div className="enq-detail-field">
                                    <div className="enq-detail-label">EMAIL</div>
                                    <div className="enq-detail-value">
                                        <a href={`mailto:${selectedEnq.email}`} className="enq-detail-link">{selectedEnq.email}</a>
                                    </div>
                                </div>
                            )}
                            {selectedEnq.phone && (
                                <div className="enq-detail-field">
                                    <div className="enq-detail-label">PHONE</div>
                                    <div className="enq-detail-value">
                                        <a href={`tel:${selectedEnq.phone}`} className="enq-detail-link">{selectedEnq.phone}</a>
                                    </div>
                                </div>
                            )}
                            {selectedEnq.postcode && (
                                <div className="enq-detail-field">
                                    <div className="enq-detail-label">POSTCODE</div>
                                    <div className="enq-detail-value">{selectedEnq.postcode}</div>
                                </div>
                            )}
                            {selectedEnq.product_name && (
                                <div className="enq-detail-field">
                                    <div className="enq-detail-label">PRODUCT</div>
                                    <div className="enq-detail-value enq-detail-value-bold">{selectedEnq.product_name}</div>
                                    {selectedEnq.stock_number && <div className="enq-detail-stock">{selectedEnq.stock_number}</div>}
                                </div>
                            )}
                            <div className="enq-detail-field">
                                <div className="enq-detail-label">MESSAGE</div>
                                <div className="enq-detail-message">{selectedEnq.message}</div>
                            </div>

                            {!selectedEnq.deleted_at && (
                                <button
                                    onClick={() => toggleReplied(selectedEnq)}
                                    className={`enq-detail-replied-btn ${selectedEnq.replied ? 'enq-detail-replied-done' : 'enq-detail-replied-pending'}`}
                                >
                                    {selectedEnq.replied ? '✓ Marked as Replied — Click to Undo' : 'Mark as Replied'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Purge modal ── */}
            {purgeModal && (
                <div className="enq-modal-overlay">
                    <div className="enq-modal">
                        <h3>⚠️ Permanent Delete</h3>
                        <p>
                            This will permanently delete{' '}
                            <strong>{purgeModal.ids.length === 1 ? '1 enquiry' : `${purgeModal.ids.length} enquiries`}</strong>.
                            This cannot be undone.
                        </p>
                        <p>Type <strong>DELETE</strong> to confirm:</p>
                        <input
                            type="text"
                            value={purgeInput}
                            onChange={(e) => { setPurgeInput(e.target.value); setPurgeError(''); }}
                            placeholder="Type DELETE"
                            className={`enq-modal-input${purgeError ? ' enq-modal-input-error' : ''}`}
                        />
                        {purgeError && <p className="enq-modal-error">{purgeError}</p>}
                        <div className="enq-modal-actions">
                            <button
                                onClick={confirmPurge}
                                disabled={purgeInput !== 'DELETE'}
                                className="enq-modal-btn enq-modal-btn-confirm"
                            >
                                Permanently Delete
                            </button>
                            <button
                                onClick={() => setPurgeModal(null)}
                                className="enq-modal-btn enq-modal-btn-cancel"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EnquiriesAdmin;